import { AlertTriangle } from 'lucide-react'
import clsx from 'clsx'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { usePatchAudit } from '@/hooks/usePatching'
import { formatDateTime } from '@/utils/format'
import styles from './Patching.module.css'

function compactJson(value: unknown): string {
  try {
    const text = JSON.stringify(value, null, 2)
    return text.length > 2000 ? `${text.slice(0, 2000)}…` : text
  } catch {
    return String(value)
  }
}

/** Append-only event list (GET campaigns/{id}/audit/). Loaded only while its tab is open. */
export function AuditTrail({ campaignId, active }: { campaignId: string; active: boolean }) {
  const { data, isLoading, isError, error } = usePatchAudit(campaignId, active)

  if (isLoading) return <LoadingSpinner label="Loading audit trail…" />
  if (isError) {
    return (
      <div className={clsx(styles.alert, styles.alertError)} role="alert" data-testid="audit-error">
        <AlertTriangle size={16} className={styles.alertIcon} />
        <span>{error.message}</span>
      </div>
    )
  }
  if (!data || data.length === 0) return <p className={styles.muted}>No audit events recorded yet.</p>

  return (
    <div className={styles.tableWrap} data-testid="audit-trail">
      <table className={styles.table}>
        <thead>
          <tr>
            <th>When</th>
            <th>Actor</th>
            <th>Action</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {data.map((event) => (
            <tr key={event.id} data-testid="audit-row">
              <td>{formatDateTime(event.created_at)}</td>
              <td>{event.actor}</td>
              <td className={styles.mono}>{event.action}</td>
              <td>
                {event.before === undefined && event.after === undefined ? (
                  <span className={styles.muted}>—</span>
                ) : (
                  <details className={styles.details}>
                    <summary>Before / after</summary>
                    {event.before !== undefined && <pre className={styles.pre}>{compactJson(event.before)}</pre>}
                    {event.after !== undefined && <pre className={styles.pre}>{compactJson(event.after)}</pre>}
                  </details>
                )}
                {event.request_id && <span className={styles.cellSub}>request {event.request_id}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
